import {
  ListingLoanInfo,
  ClientEventName,
  ClientEventSource,
  BucketedMonthlyHomeExpenses
} from '../../../../api/gqlaxy/gql/generated/graphql'
import Section from '../Section/Section'
import { Waypoint } from 'react-waypoint'
import { useIntl, defineMessages } from 'react-intl'
import { useCreateClientEvent } from '../../../../hooks'
import { useCallback, useEffect, useState } from 'react'
import { AssumablePaymentToggle } from './AssumablePaymentToggle'
import { useTrackingContext } from '../../../../providers/tracking'
import { useTrackingDebounceSetters } from '../../../../hooks/tracking'
import { Flex, Heading, Text, Stack, Box } from '@homebotapp/hb-react-component-catalog'
import { EstimatedMonthlyPaymentBreakdown } from './EstimatedMonthlyPaymentBreakdown/EstimatedMonthlyPaymentBreakdown'
import { LISTINGS_DETAILED, ESTIMATED_PAYMENT_BREAKDOWN } from '../../../../constants/tracking/trackingEventConstants'

export const MSG = defineMessages({
  head: {
    id: 'estimatedMonthlyPayment.head',
    defaultMessage: 'Your estimated payment'
  },
  subhead: {
    id: 'estimatedMonthlyPayment.subhead',
    defaultMessage: 'Play with the numbers to fit your needs'
  },
  pmt: {
    id: 'estimatedMonthlyPayment.pmt',
    defaultMessage: '{pmt} / month'
  },
  hoaSpelledOut: {
    id: 'estimatedMonthlyPayment.hoaSpelledOut',
    defaultMessage: "Homeowner's Association"
  },
  assumableToggle: {
    id: 'estimatedMonthlyPayment.assumableToggle',
    defaultMessage: 'Include assumable loan'
  }
})

export interface EstimatedMonthlyPaymentProps {
  address?: string
  homePrice: number
  listingId?: string
  listingZipCode?: string
  loanInfo?: ListingLoanInfo | null
  bucketedMonthlyHomeExpenses?: BucketedMonthlyHomeExpenses | null
  isAssumable?: boolean
  assumableToggle?: boolean
  isCalculatorsPage?: boolean
  setAssumableToggle?: (isAssumable: boolean) => void
  assumableMortgageMonthlyHomeExpenses?: BucketedMonthlyHomeExpenses | null
  assumableMortgageLoanInfo?: ListingLoanInfo | null
  assumableMortgageSecondLoanInfo?: ListingLoanInfo | null
}

export const EstimatedMonthlyPayment = ({
  address,
  loanInfo,
  listingId,
  homePrice,
  bucketedMonthlyHomeExpenses,
  isAssumable,
  listingZipCode,
  assumableToggle,
  setAssumableToggle,
  assumableMortgageMonthlyHomeExpenses,
  assumableMortgageLoanInfo,
  assumableMortgageSecondLoanInfo,
  isCalculatorsPage = false
}: EstimatedMonthlyPaymentProps) => {
  const intl = useIntl()
  const { trackingClient } = useTrackingContext()
  const [hasUsedMortgageCalculator, setHasUsedMortgageCalculator] = useState(false)
  const { mutateAsync: createClientEvent } = useCreateClientEvent({ disableErrorPrompts: true })
  const [trackModuleViewed] = useTrackingDebounceSetters(
    () =>
      trackingClient?.moduleViewed({
        event_source: LISTINGS_DETAILED,
        module_name: ESTIMATED_PAYMENT_BREAKDOWN
      })
  )

  const createUsedMortgageCalculatorEvent = useCallback(() => {
    createClientEvent({
      clientEventInput: {
        eventName: ClientEventName.UsedMortgageCalculator,
        eventSource: isCalculatorsPage ? ClientEventSource.Calculators : ClientEventSource.ListingDetailed,
        listingId: listingId || undefined
      }
    })
  }, [createClientEvent, listingId])

  useEffect(() => {
    hasUsedMortgageCalculator && createUsedMortgageCalculatorEvent()
  }, [hasUsedMortgageCalculator])

  useEffect(() => {
    setHasUsedMortgageCalculator(false)
  }, [listingId, homePrice])

  return (
    <Section>
      <Section.Body>
        <Waypoint onEnter={trackModuleViewed} bottomOffset='25%' />
        <Stack direction='column' alignItems='center' justifyContent='space-between' gap={1}>
          <Flex w='100%'>
            <Stack direction='column' alignItems='center' width='100%'>
              <Flex mb={2} alignItems='center' direction='column'>
                <Heading as='h4' size='md' fontWeight='bold' textAlign='left'>
                  {intl.formatMessage(MSG.head)}
                </Heading>
                <Text textAlign='center' size='xs'>
                  {intl.formatMessage(MSG.subhead)}
                </Text>
              </Flex>
              <AssumablePaymentToggle
                isAssumable={Boolean(isAssumable)}
                assumableToggle={assumableToggle}
                setAssumableToggle={setAssumableToggle}
              />
            </Stack>
          </Flex>
          <Box opacity={1} height='auto' width='100%'>
            <EstimatedMonthlyPaymentBreakdown
              address={address}
              loanInfo={loanInfo}
              listingId={listingId}
              homePrice={homePrice}
              listingZipCode={listingZipCode}
              assumableToggle={assumableToggle}
              isCalculatorsPage={isCalculatorsPage}
              assumableMortgageLoanInfo={assumableMortgageLoanInfo}
              bucketedMonthlyHomeExpenses={bucketedMonthlyHomeExpenses}
              setHasUsedMortgageCalculator={setHasUsedMortgageCalculator}
              assumableMortgageSecondLoanInfo={assumableMortgageSecondLoanInfo}
              assumableBucketedMonthlyHomeExpenses={assumableMortgageMonthlyHomeExpenses}
            />
          </Box>
        </Stack>
      </Section.Body>
    </Section>
  )
}
